import { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  MailOutlined,
  SearchOutlined,
  SendOutlined,
  StarFilled,
} from '@ant-design/icons';
import { Alert, App, Button, Empty, Input, Modal, Spin, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  AssignedPerson,
  OperationRequest,
  PublishAssignmentResult,
  PublishPreview,
  PublishRecipient,
  RecipientShift,
} from 'models/OperationRequest';

import { OperationRequirementService } from 'services/OperationRequirementService';

import { initials, personName } from '../data/assignment';

import './PublishAssignmentModal.scss';

interface PublishAssignmentModalProps {
  open: boolean;
  request: OperationRequest;
  storesCount: number;
  vipStores: number;
  turnosCount: number;
  filled: number;
  total: number;
  supervisors: string[];
  slots: Record<string, AssignedPerson[]>;
  forced: Record<string, string>;
  onCancel: () => void;
  onPublished: (result: PublishAssignmentResult) => void;
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

function PublishAssignmentModal({
  open,
  request,
  storesCount,
  vipStores,
  turnosCount,
  filled,
  total,
  supervisors,
  slots,
  forced,
  onCancel,
  onPublished,
}: PublishAssignmentModalProps) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board.publish_modal';
  const boardBase =
    'scenes.requirements_operation.components.assign_people.board';

  const [preview, setPreview] = useState<PublishPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [term, setTerm] = useState('');

  const loadPreview = async () => {
    setLoading(true);
    setLoadFailed(false);
    const response = await OperationRequirementService.getPublishPreview(
      request.id,
      { slots, forced }
    );
    setLoading(false);
    if (response.success) {
      setPreview(response.data as PublishPreview);
    } else {
      setLoadFailed(true);
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.preview`),
        3.5
      );
    }
  };

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setTerm('');
    loadPreview();
  }, [open]);

  const recipients = preview?.recipients || [];
  const withEmail = recipients.filter((r) => !!r.email).length;
  const visible = useMemo(() => {
    const q = normalize(term.trim());
    if (!q) return recipients;
    return recipients.filter((r) =>
      normalize(`${r.firstName} ${r.lastName} ${r.email || ''}`).includes(q)
    );
  }, [recipients, term]);

  const handlePublish = async () => {
    setPublishing(true);
    const response = await OperationRequirementService.publishAssignment(
      request.id,
      { slots, forced }
    );
    setPublishing(false);
    if (response.success) {
      onPublished(response.data as PublishAssignmentResult);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.publish`),
        3.5
      );
    }
  };

  const dateLabel = (shift: RecipientShift) => {
    const day = t(`${boardBase}.days.${shift.day}`);
    if (!shift.date) return day;
    const [y, m, d] = shift.date.split('-').map(Number);
    const date = new Date(y, m - 1, d).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    });
    return `${day} ${date}`;
  };

  const details: { key: string; value: ReactNode }[] = [
    {
      key: 'id',
      value: <strong className="publish-modal__id">{request.id}</strong>,
    },
    { key: 'client', value: request.brand },
    { key: 'campaign', value: request.campaign },
    {
      key: 'type',
      value: t(`scenes.requirements_operation.scene.contract.${request.type}`),
    },
    { key: 'week', value: request.week },
    { key: 'profile', value: request.profile },
    {
      key: 'stores',
      value: (
        <span className="publish-modal__inline">
          {t(`${i18nBase}.stores_count`, { count: storesCount })}
          {vipStores > 0 && (
            <span className="publish-modal__vip">
              <StarFilled /> {vipStores} VIP
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'turns',
      value: t(`${i18nBase}.turns_slots`, {
        turns: turnosCount,
        filled,
        total,
      }),
    },
    { key: 'supervisors', value: supervisors.join(', ') },
  ].filter((row) => row.value !== '' && row.value != null);

  const renderRecipient = (recipient: PublishRecipient) => (
    <li key={recipient.leadId} className="publish-modal__person">
      <div className="publish-modal__person-head">
        <span className="publish-modal__avatar" aria-hidden>
          {initials(recipient)}
        </span>
        <div className="publish-modal__person-info">
          <span className="publish-modal__person-name">
            {personName(recipient)}
          </span>
          {recipient.email ? (
            <span className="publish-modal__person-email">
              <MailOutlined /> {recipient.email}
            </span>
          ) : (
            <Tag color="error" className="publish-modal__no-email">
              {t(`${i18nBase}.no_email`)}
            </Tag>
          )}
        </div>
        <Tag className="publish-modal__shift-count">
          {t(`${i18nBase}.shifts_count`, { count: recipient.shifts.length })}
        </Tag>
      </div>
      <ul className="publish-modal__shifts">
        {recipient.shifts.map((shift) => (
          <li key={shift.key} className="publish-modal__shift">
            <div className="publish-modal__shift-when">
              <strong>{dateLabel(shift)}</strong>
              <span>
                {`${t(`${i18nBase}.blocks.${shift.block}`)} · ${shift.from} – ${
                  shift.to
                }`}
              </span>
            </div>
            <div className="publish-modal__shift-where">
              <strong>
                {shift.storeName}
                {shift.chain && (
                  <span className="publish-modal__muted">{` · ${shift.chain}`}</span>
                )}
              </strong>
              <span>
                {[shift.address, shift.location].filter(Boolean).join(', ')}
              </span>
            </div>
            <div className="publish-modal__shift-sup">
              <span className="publish-modal__muted">
                {t(`${i18nBase}.supervisor`)}
              </span>
              <span>{shift.supervisor || '—'}</span>
            </div>
          </li>
        ))}
      </ul>
    </li>
  );

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      width={860}
      centered
      className="publish-modal"
      maskClosable={!publishing}
      closable={!publishing}
      styles={{ body: { padding: 0 } }}
    >
      <div className="publish-modal__shell">
        <header className="publish-modal__head">
          <span className="publish-modal__icon" aria-hidden>
            <SendOutlined />
          </span>
          <div className="publish-modal__head-text">
            <h2 className="publish-modal__title">{t(`${i18nBase}.title`)}</h2>
            <p className="publish-modal__description">
              {t(`${i18nBase}.description`)}
            </p>
          </div>
        </header>

        <div className="publish-modal__scroll">
          <section>
            <h3 className="publish-modal__section-title">
              {t(`${i18nBase}.sections.request`)}
            </h3>
            <dl className="publish-modal__grid">
              {details.map((row) => (
                <div key={row.key} className="publish-modal__row">
                  <dt>{t(`${i18nBase}.labels.${row.key}`)}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {loading && (
            <div className="publish-modal__loading">
              <Spin />
              <span>{t(`${i18nBase}.loading`)}</span>
            </div>
          )}

          {!loading && loadFailed && (
            <Alert
              type="error"
              showIcon
              message={t(`${i18nBase}.errors.preview`)}
              action={
                <Button size="small" onClick={loadPreview}>
                  {t(`${i18nBase}.buttons.retry`)}
                </Button>
              }
            />
          )}

          {!loading && preview && (
            <>
              <Alert
                type={preview.notifyEnabled ? 'warning' : 'info'}
                showIcon
                message={t(
                  `${i18nBase}.lock_notice.${
                    preview.notifyEnabled ? 'notify' : 'publish_only'
                  }`
                )}
              />
              {preview.notifyEnabled && (
                <section>
                  <h3 className="publish-modal__section-title">
                    {t(`${i18nBase}.sections.email`)}
                  </h3>
                  <dl className="publish-modal__grid">
                    <div className="publish-modal__row">
                      <dt>{t(`${i18nBase}.labels.from`)}</dt>
                      <dd>
                        {`${preview.sender.senderName} <${preview.sender.from}>`}
                      </dd>
                    </div>
                    <div className="publish-modal__row">
                      <dt>{t(`${i18nBase}.labels.subject`)}</dt>
                      <dd>{preview.subject}</dd>
                    </div>
                  </dl>
                </section>
              )}

              <section>
                <div className="publish-modal__people-head">
                  <h3 className="publish-modal__section-title">
                    {t(
                      `${i18nBase}.sections.${
                        preview.notifyEnabled ? 'people' : 'people_assigned'
                      }`,
                      {
                        count: recipients.length,
                      }
                    )}
                  </h3>
                  {recipients.length > 4 && (
                    <Input
                      allowClear
                      size="small"
                      prefix={<SearchOutlined />}
                      placeholder={t(`${i18nBase}.search`)}
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      className="publish-modal__search"
                    />
                  )}
                </div>
                {preview.notifyEnabled && preview.missingEmails > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    className="publish-modal__alert"
                    message={t(`${i18nBase}.missing_emails`, {
                      count: preview.missingEmails,
                    })}
                  />
                )}
                {visible.length ? (
                  <ul className="publish-modal__people">
                    {visible.map(renderRecipient)}
                  </ul>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t(`${i18nBase}.no_results`)}
                  />
                )}
              </section>
            </>
          )}
        </div>

        <footer className="publish-modal__actions">
          <Button disabled={publishing} onClick={onCancel}>
            {t(`${i18nBase}.buttons.cancel`)}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={publishing}
            disabled={!preview || loading}
            onClick={handlePublish}
          >
            {preview?.notifyEnabled === false
              ? t(`${i18nBase}.buttons.publish_only`)
              : t(`${i18nBase}.buttons.confirm`, { count: withEmail })}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}

export default PublishAssignmentModal;
